import React from 'react'
import styled from '@emotion/styled'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { AppRoutes } from '@core/AppRoutes'
import { SvgLazy } from '@mobi/component-library/Common/Svg/SvgLazy'
import { Icon } from '@mobi/component-library/Common/Icon'
import { font, spacing, colors } from '@mobi/component-library/Theme/Common'
import { pluralize } from '@mobi/utils/string/pluralize'
import { colors as mobiColors } from '@mobi/settings/colors'
import { ProfileAvatar } from '@core/Areas/BetLoop/Components/Common/ProfileAvatar'
import { FlameStreakBadge } from '@core/Areas/BetLoop/Components/Common/FlameStreak'
import { FollowButton } from '@core/Areas/BetLoop/Components/Common/FollowButton'
import { betLoopPrincipleProfileQuery } from '@core/Areas/BetLoop/helpers/queries'
import type { BetLoopProfile } from '@core/Areas/BetLoop/types'
import type { FollowButtonLocation } from '@core/Areas/BetLoop/Components/Common/FollowButton/types'
import { trackBetLoopViewMyProfile, trackBetLoopViewProfile } from '@core/Areas/BetLoop/helpers/analytics'

type ProfileItemProps = {
  profile: BetLoopProfile
  location: FollowButtonLocation
  searchQuery?: string
}

export const ProfileItem: React.FC<ProfileItemProps> = ({ profile, location, searchQuery }) => {
  const { data: myProfile } = useQuery(betLoopPrincipleProfileQuery)

  const navigate = useNavigate()

  if (myProfile === undefined) return null

  const isMyProfile = myProfile?.id === profile.id

  const handleProfileClick = () => {
    if (isMyProfile) {
      trackBetLoopViewMyProfile()
      navigate(AppRoutes.BetLoopMyProfile)
    } else {
      trackBetLoopViewProfile()
      navigate(`${AppRoutes.BetLoopProfile}/${profile.id}`)
    }
  }

  const shouldShouldDisplayWinStreak = profile && profile.currentWinStreak >= 3

  return (
    <ContainerStyled data-testid='ProfileItem'>
      <div onClick={handleProfileClick}>
        {profile && <ProfileAvatar avatar={profile.avatar} color={profile.color} />}

        <div className='profile-item__profile-info'>
          {location === 'search-page' ? (
            <span style={{ fontWeight: font.weight.medium }}>
              {matchSearchQuery(profile.displayName, searchQuery)}
            </span>
          ) : (
            <span>{profile.displayName}</span>
          )}
          <span>{`${profile.followerCount} ${pluralize(profile.followerCount, 'Follower')}`}</span>
        </div>
      </div>

      <div>
        {shouldShouldDisplayWinStreak && <FlameStreakBadge value={profile.currentWinStreak} />}

        {profile.type === 'ExpertTipster' && (
          <Icon type='tabtouchLogoCompact' size='2.5em' color={mobiColors.brand.primary} />
        )}

        {profile.type === 'GiddyUp' && (
          <SvgLazy name='GiddyUpLogoCompact' color='#0051A0' width='3.5em' />
        )}

        {!isMyProfile ? (
          <div onClick={e => e.stopPropagation()}>
            <FollowButton
              accountId={profile.id}
              accountDisplayName={profile.displayName}
              isFollowing={profile.isFollowing}
              isNotificationsEnabled={profile.isNotificationsEnabled}
              location={location}
            />
          </div>
        ) : null}
      </div>
    </ContainerStyled>
  )
}

// =============
// Local Helpers
// =============

const matchSearchQuery = (displayName: string, searchQuery?: string) => {
  if (!searchQuery || searchQuery.length < 2) return displayName

  const search = searchQuery.toLowerCase()
  const name = displayName.toLowerCase()

  let match = ''
  for (let i = searchQuery.length; i > 0; i--) {
    const sub = search.slice(0, i)
    if (name.includes(sub)) {
      match = sub
      break
    }
  }

  if (!match) return displayName

  const regex = new RegExp(`(${match})`, 'gi')
  const parts = displayName.split(regex)

  return parts.map((part, i) =>
    regex.test(part) ? (
      <span key={i} style={{ fontWeight: font.weight.semibold }}>
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    )
  )
}

// ======
// Styles
// ======

const ContainerStyled = styled.article({
  display: 'flex',
  flexDirection: 'row',
  gap: spacing.smx1,
  padding: spacing.sm,
  backgroundColor: colors.white,
  fontFamily: font.family.primary,
  justifyContent: 'space-between',
  borderBottom: `1px solid ${colors.neutral[200]}`,
  WebkitTapHighlightColor: 'transparent',
  transition: 'background-color 0.15s ease',

  '@media (pointer: fine)': {
    ':hover': { backgroundColor: colors.neutral[100], cursor: 'pointer' },
  },

  // prevent parent active style when pressing a button
  '&:active:not(:has(button:active), :has(button:hover))': { backgroundColor: colors.neutral[300] },

  // Avatar and Profile Info
  '> div:first-of-type': {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.smx1,
    background: 0,
    border: 0,
    padding: 0,
    cursor: 'pointer',
    opacity: 1,
    transition: 'opacity 0.25s ease',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    flex: '1 1 auto',
    minWidth: 0,

    '> :first-of-type': { flex: '0 0 auto' },

    '.profile-item__profile-info': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      flex: '1 1 auto',
      minWidth: 0,

      '.profile-item__display-name': {
        fontWeight: font.weight.semibold,
      },

      '> span:first-of-type': {
        display: 'block',
        width: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: font.size.lg.fontSize,
        fontWeight: font.weight.medium,
        lineHeight: font.size.lg.lineHeight,
        color: colors.black,
      },

      '> span:last-of-type': {
        fontSize: font.size.md.fontSize,
        fontWeight: font.weight.regular,
        lineHeight: font.size.md.lineHeight,
        color: colors.neutral[800],
      },
    },
  },

  // Action Buttons
  '> div:last-of-type': {
    display: 'flex',
    alignItems: 'center',
    flex: '0 0 auto',
    gap: spacing.smx1,

    '> div': {
      display: 'flex',
      alignItems: 'center',
      flex: '0 0 auto',
      gap: spacing.smx2,
    },
  },
})
